/**
 * Generates preview succession items for new successions added during inline crop edit.
 *
 * Works entirely with week-format data (phases/tasks with `start` in weeks from timeline start,
 * `len` in weeks) so it can be used directly in the displayCrops memo without extra conversion.
 *
 * Follows the same offsetting pattern as generatePreviewScheduleData's succession block:
 * each successive planting is offset by baseIntervalDays from the previous one.
 *
 * @param {Object} params
 * @param {Array}  params.existingChildren  - Current succession children (week-format phases/tasks)
 * @param {number} params.newCount          - Target total succession count (> existingChildren.length)
 * @param {number} params.baseIntervalDays  - Days between each planting (derived from existing data)
 * @param {string} params.growthType        - 'indeterminate' | 'determinate' | null
 * @param {number} params.firstFrostWeek    - Frost date in weeks from timeline start (null if unknown)
 * @returns {Array} Preview child objects, shape matches succession child entries in displayCrops
 */
export function generateNewSuccessionPreview({ existingChildren, newCount, baseIntervalDays, growthType = null, firstFrostWeek = null, baseCrop = null }) {
  if (!existingChildren || newCount <= existingChildren.length) return []

  const lastChild = existingChildren[existingChildren.length - 1] ?? baseCrop
  if (!lastChild) return []

  const weekOffset = baseIntervalDays / 7
  const previewChildren = []

  for (let i = existingChildren.length; i < newCount; i++) {
    const successionNum = i + 1  // 1-based
    const stepsFromLast = successionNum - existingChildren.length

    const previewPhases = (lastChild.phases ?? []).map(phase => {
      const newStart = phase.start + weekOffset * stepsFromLast
      let newLen = phase.len

      if (growthType === 'indeterminate' && phase.phase_type_code === 'mature' && firstFrostWeek != null) {
        newLen = Math.max(1 / 7, firstFrostWeek - newStart)
      }

      return {
        ...phase,
        id: `preview-succ-${successionNum}-${phase.step ?? phase.phase_type_code}`,
        start: newStart,
        len: newLen,
      }
    })

    const previewTasks = (lastChild.tasks ?? []).map((task, tidx) => ({
      ...task,
      id: `preview-task-succ-${successionNum}-${tidx}`,
      start: task.start + weekOffset * stepsFromLast,
    }))

    previewChildren.push({
      id: `preview-succession-${successionNum}`,
      isPreview: true,
      succession: successionNum,
      interval_days: successionNum * baseIntervalDays,
      phases: previewPhases,
      tasks: previewTasks,
    })
  }

  return previewChildren
}
